import { Button } from '@farol-ds/react'
import { SocialSignInButton } from '@jusbrasil-web/shared-social-sign-in'
import { FormEvent, useState } from 'react'

import { JUSID_PLATFORM_WEB_AUTH } from '../../constants'
import type { SignupModalHeaderCopy } from '../../experiments/signup-modal/highlight-copy'
import { parseErrorMessage } from '../../utils/parseErrorMessage'
import { getEmailErrorMessage } from '../../utils/validation'
import { EmailField } from '../email-field'
import { LoginCard } from '../login-card'
import styles from './email-sign-in.module.scss'

interface EmailSignInProps {
  initialEmail?: string
  isLoading?: boolean
  error?: string
  onSubmit: (email: string) => Promise<void>
  appId?: string
  featureCode?: string
  fromComponent?: string
  nextUrl?: string
  scope?: string
  onCloseClick?: () => void
  signupModalHeader?: SignupModalHeaderCopy
}

export function EmailSignIn({
  initialEmail = '',
  isLoading = false,
  error,
  onSubmit,
  appId,
  featureCode,
  fromComponent,
  nextUrl,
  scope,
  onCloseClick,
  signupModalHeader,
}: EmailSignInProps) {
  const [emailValue, setEmailValue] = useState(initialEmail)
  const [emailTouched, setEmailTouched] = useState(false)
  const [emailError, setEmailError] = useState<string | undefined>()

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setEmailTouched(true)
    const message = getEmailErrorMessage(emailValue)
    setEmailError(message)
    if (message) return
    await onSubmit(emailValue)
  }

  const parsedError = error ? parseErrorMessage(error) : null

  const header = {
    title: signupModalHeader?.title ?? 'Entre ou crie sua conta',
    ...(signupModalHeader?.subtitle ? { subtitle: { content: signupModalHeader.subtitle } } : {}),
    ...(parsedError && {
      errorMessage: {
        title: parsedError.title,
        description: parsedError.description,
      },
    }),
  }

  return (
    <LoginCard header={header} onCloseClick={onCloseClick}>
      <form onSubmit={handleSubmit} className={styles.form} noValidate>
        <EmailField
          value={emailValue}
          onChange={setEmailValue}
          error={emailTouched ? emailError : undefined}
          disabled={isLoading}
        />

        <Button type="submit" kind="primary" loading={isLoading} className={styles.submitButton}>
          Continuar
        </Button>
      </form>

      <div className={styles.divider}>
        <span className={styles.dividerText}>ou</span>
      </div>

      <div className={styles.socialButtons} role="region" aria-label="Entrar por redes sociais">
        <SocialSignInButton
          appId={appId ?? JUSID_PLATFORM_WEB_AUTH}
          featureCode={featureCode ?? ''}
          fromComponent={fromComponent ?? 'EmailSignIn'}
          minHeight={48}
          nextUrl={nextUrl ?? ''}
          scope={scope}
          provider="google"
        >
          Continuar com Google
        </SocialSignInButton>
      </div>
    </LoginCard>
  )
}
